import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { trpc } from "@/lib/trpc";
import { isNative, nativeGoogleSignIn } from "../native";
import { getLoginUrl, isManusLoginConfigured } from "@/const";
import symbolInk from "@/assets/brand/retayf-symbol-ink.svg";
import symbolPaper from "@/assets/brand/retayf-symbol-paper.svg";

/**
 * Signed-out landing + sign-in screen. Extracted from the legacy Canvas so the
 * NEW tabbed shell can gate on it too: an unauthenticated visitor (e.g. a
 * private-browsing tab) must always land here — never on a dead app shell with
 * placeholder data. Hosts both sign-in branches: the Manus OAuth portal button
 * and Google (web redirect, or the OS-native account picker inside the
 * Capacitor shell, where Google blocks embedded-webview OAuth).
 */
export default function Landing() {
  const { t, locale } = useI18n();
  const providersQuery = trpc.auth.providers.useQuery();

  // On the native shell we use the OS-native Google account picker
  // (Google blocks OAuth inside embedded webviews), not the web redirect
  // flow. Detect the platform so the Google button branches accordingly.
  const [native, setNative] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  useEffect(() => {
    isNative().then(setNative).catch(() => setNative(false));
  }, []);

  const handleNativeGoogle = async () => {
    if (signingIn) return;
    setSigningIn(true);
    try {
      await nativeGoogleSignIn();
      // nativeGoogleSignIn navigates to "/" on success.
    } catch (err) {
      console.warn("[native] Google sign-in failed", err);
      toast(
        locale === "ar"
          ? "تعذّر تسجيل الدخول عبر Google. حاول مرة أخرى."
          : "Google sign-in failed. Please try again.",
      );
      setSigningIn(false);
    }
  };
  // NOTE: we intentionally do NOT pass a `nativeReturn` deep-link param
  // anymore. The native shell now keeps Google OAuth INSIDE the webview
  // (see `server.allowNavigation` + `overrideUserAgent` in
  // capacitor.config.ts), so the OAuth round-trip completes at the
  // receipt1.duckdns.org origin and the server's callback sets the
  // session cookie first-party — exactly like the web. The old
  // custom-scheme deep-link return (`com.receipt.app://auth/complete`)
  // was unreliable on iOS (external-browser cookie jar, URL fragment
  // corruption, cold-launch event timing) and is no longer used.
  const nativeReturnParam = "";
  return (
    <div className="min-h-screen grid place-items-center bg-background">
      <div className="container max-w-2xl text-center py-20">
        {/* The D3 mark, ink on the paper background — the primary Retayf
            treatment. It replaced a coral tile holding the literal letter "R".
            Same 64px box, same position, same margin. */}
        <img
          src={symbolInk}
          alt=""
          aria-hidden="true"
          data-testid="landing-brand-mark"
          className="mx-auto h-16 w-16 mb-6 dark:hidden"
          draggable={false}
        />
        <img
          src={symbolPaper}
          alt=""
          aria-hidden="true"
          className="mx-auto h-16 w-16 mb-6 hidden dark:block"
          draggable={false}
        />
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">
          {t.appName} — {t.tagline}
        </h1>
        <p className="mt-4 text-muted-foreground text-lg leading-relaxed">
          {locale === "ar"
            ? "ملخّصات يومية تُكتب من تلقاء نفسها من أدواتك. تعتمدها بضغطة واحدة. لا نماذج، لا جداول."
            : "Daily summaries that write themselves from your tools. Approve with one tap. No forms, no spreadsheets."}
        </p>
        <div className="mt-8 flex flex-col items-center gap-3">
          {isManusLoginConfigured() && (
            <a
              href={getLoginUrl()}
              className="inline-flex items-center justify-center rounded-full bg-coral text-white px-6 py-3 text-base font-medium shadow-lg shadow-coral/20 hover:opacity-95 transition-opacity min-w-[220px]"
            >
              {locale === "ar" ? "ابدأ" : "Get started"}
            </a>
          )}
          {providersQuery.data?.google &&
            (native ? (
              <button
                type="button"
                onClick={handleNativeGoogle}
                disabled={signingIn}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-card text-foreground px-6 py-3 text-base font-medium hover:bg-muted transition-colors min-w-[220px] disabled:opacity-60"
                aria-label={t.signInWithGoogle}
              >
                {signingIn ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <GoogleIcon className="h-4 w-4" />
                )}
                {t.signInWithGoogle}
              </button>
            ) : (
              <a
                href={`/api/auth/google/start?origin=${encodeURIComponent(
                  typeof window !== "undefined" ? window.location.origin : "",
                )}${nativeReturnParam}`}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-card text-foreground px-6 py-3 text-base font-medium hover:bg-muted transition-colors min-w-[220px]"
                aria-label={t.signInWithGoogle}
              >
                <GoogleIcon className="h-4 w-4" />
                {t.signInWithGoogle}
              </a>
            ))}
          {/* While the providers query is still loading (or errored
              transiently — e.g. during a deploy recreate), show a
              neutral spinner, NOT the alarming "not configured"
              message. Only declare "no sign-in" once the query has
              actually RESOLVED and confirmed nothing is wired. */}
          {!isManusLoginConfigured() &&
            !providersQuery.data?.google &&
            (providersQuery.isLoading || providersQuery.isError) && (
              <div className="text-sm text-muted-foreground inline-flex items-center gap-2 px-5 py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                {locale === "ar" ? "جاري التحميل…" : "Loading…"}
              </div>
            )}
          {!isManusLoginConfigured() &&
            providersQuery.isSuccess &&
            !providersQuery.data?.google && (
              <div className="text-sm text-muted-foreground border border-dashed border-border rounded-2xl px-5 py-4 max-w-md">
                {locale === "ar"
                  ? "لم يتم تكوين أي طريقة لتسجيل الدخول بعد. على المسؤول إعداد متغيّر VITE_OAUTH_PORTAL_URL أو GOOGLE_CLIENT_ID ثم إعادة بناء الصورة."
                  : "No sign-in method is configured. The operator needs to set VITE_OAUTH_PORTAL_URL or GOOGLE_CLIENT_ID and rebuild the image."}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <path
        fill="#4285F4"
        d="M21.6 12.227c0-.815-.073-1.6-.21-2.354H12v4.45h5.385a4.604 4.604 0 0 1-1.997 3.022v2.51h3.232c1.892-1.742 2.98-4.305 2.98-7.628z"
      />
      <path
        fill="#34A853"
        d="M12 22c2.7 0 4.964-.895 6.62-2.422l-3.232-2.51c-.895.6-2.04.954-3.388.954-2.605 0-4.81-1.76-5.598-4.122H3.064v2.59A9.996 9.996 0 0 0 12 22z"
      />
      <path
        fill="#FBBC05"
        d="M6.402 13.9a5.997 5.997 0 0 1 0-3.8V7.51H3.064a9.996 9.996 0 0 0 0 8.98l3.338-2.59z"
      />
      <path
        fill="#EA4335"
        d="M12 5.978c1.47 0 2.788.505 3.825 1.497l2.868-2.868C16.96 2.99 14.696 2 12 2A9.996 9.996 0 0 0 3.064 7.51l3.338 2.59C7.19 7.738 9.395 5.978 12 5.978z"
      />
    </svg>
  );
}
